import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import { Ledger, StatusCell, What, Who } from '../shared/cells';
import { fetch_payout_details_awaiting, fetch_payout_details_pending } from '../api';
import {
  awaiting_filter_summary,
  awaiting_view,
  pending_view,
  review_error,
  since_filter_next,
  since_filters_on,
  since_month_error
} from './adapters';
import { FilterBar, Segmented } from '../shared/controls';
import { summary_line } from '../payouts/summary_line';
import ReviewDialog from './review_dialog';
import {
  PAYMENT_DETAILS_URL_KEYS,
  VIEW_AWAITING,
  VIEW_PENDING,
  parse_payment_details_filters,
  payment_details_query
} from './url_filters';
import { useInitialQuery, useQueryMirror } from '../shared/use_query_mirror';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { collect_all_pages } from '../../../util_components/csv_export/collect_pages';
import { auth_only } from '../payouts/csv';
import { AWAITING_COLUMNS, PENDING_COLUMNS, details_csv_name } from './csv';
import './payment_details.css';

/*
 * the Payment details tab (/teacher-payments/payment-details): the submissions waiting for review
 * (oldest first, paged by next_after_id) and the teachers with no approved details.
 */
const PENDING_FAILED_TEXT = 'Could not load the payment details waiting for review.';
const AWAITING_FAILED_TEXT = 'Could not load the teachers awaiting payment details.';
const SINCE_DEBOUNCE_MS = 300;

const review_button = (r, detail_uuid, on_review, className) => (
  <button
    type="button"
    className={className}
    aria-label={`Review payment details of ${r.teacher_name}`}
    onClick={() => on_review(detail_uuid)}
  >
    Review
  </button>
);

const flag_pills = (r) => {
  const pills = [];
  if (r.is_change) pills.push(<StatusPill tone="blue">Replaces approved details</StatusPill>);
  if (r.shared_account) pills.push(<StatusPill tone="red">Shared account</StatusPill>);
  if (!pills.length) pills.push(<StatusPill tone="gray">First submission</StatusPill>);
  return (
    <div className="tp-pd-pills">
      {pills.map((p, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={i}>{p}</React.Fragment>
      ))}
    </div>
  );
};

// Four tracks (list-row pattern): teacher · account · tax · flags and Review; cards below 660px.
const pending_columns = (on_review) => [
  {
    key: 'teacher',
    role: 'identifier',
    label: 'Teacher',
    track: { compact: '28%', full: '26%' },
    render: (r) => (
      <Who name={r.teacher_name} meta={[r.teacher_email, `Submitted ${r.submitted_at}`]} />
    )
  },
  {
    key: 'holder',
    label: 'Account',
    render: (r) => (
      <What
        title={r.account_holder_name}
        lines={[
          <StatusPill tone={r.name_match.tone}>{r.name_match.label}</StatusPill>,
          <span className="tp-c-inline">
            <span className="tp-mono tp-pd-acct">{r.account_number_masked}</span>
            <span>
              {r.ifsc} · {r.bank_name}
            </span>
          </span>,
          `Branch: ${r.branch}`
        ]}
      />
    )
  },
  {
    key: 'tax',
    label: 'Tax',
    align: 'right',
    track: { compact: '128px', full: '150px' },
    render: (r) => (
      <Ledger
        items={[
          { label: 'GST', value: r.gst_registered ? r.gst_rate : 'Not registered' },
          { label: 'TDS', value: r.tds_rate }
        ]}
      />
    )
  },
  {
    key: 'flags',
    role: 'status',
    label: 'Status',
    track: { compact: '168px', full: '200px' },
    render: (r) => (
      <StatusCell
        chip={flag_pills(r)}
        actions={
          <span className="tp-c-acts tp-actions">
            {review_button(r, r.detail_uuid, on_review, 'tp-link')}
          </span>
        }
      />
    ),
    card_render: (r) => <StatusCell chip={flag_pills(r)} />
  },
  {
    key: 'actions',
    role: 'actions',
    label: 'Action',
    in_table: false,
    // Cards: a neutral button in the footer (the table keeps the link); row actions are never orange.
    card_render: (r) => review_button(r, r.detail_uuid, on_review, 'tp-btn-out')
  }
];

const awaiting_chip = (r) =>
  r.pending_detail_uuid ? (
    <StatusPill tone="orange">Submitted, waiting for review</StatusPill>
  ) : (
    <StatusPill tone="gray">Not submitted yet</StatusPill>
  );

const awaiting_columns = (on_review) => [
  {
    key: 'teacher',
    role: 'identifier',
    label: 'Teacher',
    track: { compact: '40%', full: '36%' },
    render: (r) => <Who name={r.teacher_name} meta={[r.teacher_email]} />
  },
  {
    key: 'latest_month',
    role: 'date',
    label: 'Latest payment month',
    render: (r) => <span>{r.latest_month}</span>
  },
  {
    key: 'details',
    role: 'status',
    label: 'Payment details',
    track: { compact: '200px', full: '220px' },
    render: (r) => (
      <StatusCell
        chip={awaiting_chip(r)}
        actions={
          r.pending_detail_uuid ? (
            <span className="tp-c-acts tp-actions">
              {review_button(r, r.pending_detail_uuid, on_review, 'tp-link')}
            </span>
          ) : null
        }
      />
    ),
    card_render: (r) => <StatusCell chip={awaiting_chip(r)} />
  },
  {
    key: 'actions',
    role: 'actions',
    label: 'Action',
    in_table: false,
    card_render: (r) =>
      r.pending_detail_uuid
        ? review_button(r, r.pending_detail_uuid, on_review, 'tp-btn-out')
        : null
  }
];

// One cursor-paged list: rows append on "Load more"; a reload starts over. read(cursor) -> {rows, next}.
const usePagedList = (read, failed_text, on_error) => {
  const [state, setState] = useState({ status: 'loading', rows: [], next: null, meta: {} });
  const [more_busy, setMoreBusy] = useState(false);
  const [error, setError] = useState({ kind: '', text: '' });
  const seq = useRef(0);

  const run = useCallback(
    (cursor) => {
      seq.current += 1;
      const mine = seq.current;
      if (cursor) setMoreBusy(true);
      else setState((s) => ({ ...s, status: 'loading' }));
      read(cursor)
        .then((page) => {
          if (mine !== seq.current) return;
          setMoreBusy(false);
          setError({ kind: '', text: '' });
          setState((s) => ({
            status: 'ready',
            rows: cursor ? s.rows.concat(page.rows) : page.rows,
            next: page.next,
            meta: page.meta || {}
          }));
        })
        .catch((e) => {
          if (mine !== seq.current) return;
          const v = review_error(e, failed_text);
          if (v.kind === 'auth') on_error(e, failed_text);
          setMoreBusy(false);
          setError(v);
          // A failed "Load more" keeps the rows already shown.
          if (!cursor) setState({ status: 'error', rows: [], next: null, meta: {} });
        });
    },
    [read, failed_text, on_error]
  );

  useEffect(() => {
    run(null);
  }, [run]);

  return { ...state, more_busy, error, reload: () => run(null), load_more: () => run(state.next) };
};

const read_pending = (cursor) =>
  fetch_payout_details_pending(cursor).then((d) => {
    const v = pending_view(d);
    return { rows: v.rows, next: v.next_after_id };
  });

// The list card: header (title, count, note), then the rows, then Load more.
function ListBody({ list, columns, rowKey, empty_text, title, note, acts }) {
  let body;
  if (list.status === 'loading') body = <CustomLoading />;
  else if (list.status === 'error') {
    body =
      list.error.kind === 'forbidden' ? (
        <div className="tp-banner tp-banner-info">{list.error.text}</div>
      ) : (
        <LoadFailed text={list.error.text} on_retry={list.reload} />
      );
  } else {
    body = (
      <>
        <DataTable
          responsive
          columns={columns}
          rows={list.rows}
          rowKey={rowKey}
          emptyText={empty_text}
          card_below={660}
        />
        {list.error.text ? <LoadFailed text={list.error.text} on_retry={list.load_more} /> : null}
        {list.next ? (
          <div className="tp-pd-more">
            <button
              type="button"
              className="tp-btn-out"
              onClick={list.more_busy ? undefined : list.load_more}
              aria-disabled={list.more_busy ? 'true' : undefined}
            >
              {list.more_busy ? 'Loading…' : 'Load more'}
            </button>
          </div>
        ) : null}
      </>
    );
  }
  return (
    <div className="tp-card">
      <div className="tp-card-hd">
        <span className="tp-card-hd-main">
          <span className="tp-card-hd-title" role="heading" aria-level={2}>
            {title}
          </span>
          {list.status === 'ready' ? (
            <span className="tp-card-hd-sub">
              {list.rows.length}
              {list.next ? '+' : ''} shown
            </span>
          ) : null}
        </span>
        {acts ? <span className="tp-card-hd-acts">{acts}</span> : null}
        {note ? <span className="tp-card-hd-note">{note}</span> : null}
      </div>
      {body}
    </div>
  );
}

function PaymentDetailsTab({ on_error, on_notify }) {
  const url = useInitialQuery(parse_payment_details_filters);
  const [view, setView] = useState(url.view);
  const [since_draft, setSinceDraft] = useState(url.since);
  const [since_month, setSinceMonth] = useState(url.since);
  const [reviewing, setReviewing] = useState('');

  const read_awaiting = useCallback(
    (cursor) =>
      fetch_payout_details_awaiting(since_month, cursor).then((d) => {
        const v = awaiting_view(d);
        return {
          rows: v.rows,
          next: v.next_after_teacher_id,
          meta: { since_month: v.since_month }
        };
      }),
    [since_month]
  );

  const pending = usePagedList(read_pending, PENDING_FAILED_TEXT, on_error);
  const awaiting = usePagedList(read_awaiting, AWAITING_FAILED_TEXT, on_error);
  const since_err = since_month_error(since_draft);
  const summary = awaiting_filter_summary({ list: awaiting, since_month });
  useQueryMirror(PAYMENT_DETAILS_URL_KEYS, payment_details_query({ view, since: since_month }));

  // A valid month applies once typing pauses (no Show button); an invalid one only shows its error.
  useEffect(() => {
    const next = since_filter_next(since_draft, since_month);
    if (next === null) return undefined;
    const t = setTimeout(() => setSinceMonth(next), SINCE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [since_draft, since_month]);

  const clear_since = () => {
    setSinceDraft('');
    setSinceMonth('');
  };

  const reload_all = () => {
    pending.reload();
    awaiting.reload();
  };

  const on_reviewed = (text) => {
    setReviewing('');
    on_notify('success', text);
    reload_all();
  };

  const on_stale = () => {
    setReviewing('');
    reload_all();
  };

  return (
    <div className="tp-pd-tab">
      <p className="tp-pd-lead">
        Bank and tax details teachers submit. Nobody edits them: approve or reject each one.
      </p>

      <Segmented
        label="Payment details list"
        options={[
          {
            value: VIEW_PENDING,
            label: 'Waiting for review',
            count:
              pending.status === 'ready'
                ? `${pending.rows.length}${pending.next ? '+' : ''}`
                : undefined
          },
          { value: VIEW_AWAITING, label: 'Awaiting details' }
        ]}
        value={view}
        onChange={setView}
      />

      {view === VIEW_PENDING ? (
        <ListBody
          list={pending}
          columns={pending_columns(setReviewing)}
          rowKey={(r) => r.detail_uuid}
          empty_text="No payment details are waiting for review."
          title="Waiting for review"
          note="Oldest first. Review each one before payouts."
          acts={
            <DownloadCsvButton
              columns={PENDING_COLUMNS}
              load_rows={() => collect_all_pages(read_pending)}
              file_name={details_csv_name('Waiting for review')}
              on_error={auth_only(review_error, on_error)}
            />
          }
        />
      ) : (
        <>
          <FilterBar label="Awaiting details filters" summary={summary_line(summary)}>
            <span className={`tp-fsel tp-pd-since ${since_month ? 'is-on' : ''}`.trim()}>
              <label htmlFor="tp-pd-since" className="tp-fsel-k">
                With a payment month since:
              </label>
              <input
                id="tp-pd-since"
                className="tp-pd-since-in"
                value={since_draft}
                onChange={(e) => setSinceDraft(e.target.value)}
                placeholder="YYYY-MM, e.g. 2026-10"
                aria-describedby={since_err ? 'tp-pd-since-err' : undefined}
              />
            </span>
            {since_filters_on(since_month) ? (
              <button type="button" className="tp-link tp-fbar-end" onClick={clear_since}>
                Clear filters
              </button>
            ) : null}
          </FilterBar>
          {since_err ? (
            <p id="tp-pd-since-err" className="tp-neg tp-pd-since-err" role="alert">
              {since_err}
            </p>
          ) : null}
          <ListBody
            list={awaiting}
            columns={awaiting_columns(setReviewing)}
            rowKey={(r) => r.teacher_uuid}
            empty_text="Every teacher with a payment month has approved payment details."
            title="Awaiting details"
            acts={
              <DownloadCsvButton
                columns={AWAITING_COLUMNS}
                load_rows={() => collect_all_pages(read_awaiting)}
                file_name={details_csv_name(
                  since_month ? `Awaiting details since ${since_month}` : 'Awaiting details'
                )}
                on_error={auth_only(review_error, on_error)}
              />
            }
            note={
              awaiting.status === 'ready' && awaiting.meta.since_month
                ? 'They cannot be paid until their details are approved.'
                : ''
            }
          />
        </>
      )}

      {reviewing ? (
        <ReviewDialog
          detail_uuid={reviewing}
          on_close={() => setReviewing('')}
          on_reviewed={on_reviewed}
          on_stale={on_stale}
          on_refresh={reload_all}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
}

export default PaymentDetailsTab;
